import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { createIntegrationConnection, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { azureDevOpsFormSchema, type AzureDevOpsForm } from "./integrationFormSchemas";

const textFieldLabels: Record<"name" | "organization" | "project" | "workItemType" | "tenantId" | "clientId", string> = {
  name: "Connection name",
  organization: "Organization",
  project: "Project",
  workItemType: "Work item type",
  tenantId: "Tenant ID",
  clientId: "Client ID",
};
const textFieldKeys = Object.keys(textFieldLabels) as (keyof typeof textFieldLabels)[];

export function AzureDevOpsConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const formSchema = azureDevOpsFormSchema.superRefine((values, ctx) => {
    if (!connection && !values.clientSecret) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["clientSecret"], message: "Client secret is required." });
    }
  });
  const { register, handleSubmit, formState: { errors } } = useForm<AzureDevOpsForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: connection?.name ?? "Azure DevOps",
      organization: connection?.configSummary.organization ?? "",
      project: connection?.configSummary.project ?? "",
      workItemType: connection?.configSummary.workItemType ?? "Issue",
      tenantId: connection?.configSummary.tenantId ?? "",
      clientId: connection?.configSummary.clientId ?? "",
      clientSecret: "",
    },
  });

  const submit = handleSubmit((values) => action.run("save", async () => {
    const config = {
      organization: values.organization,
      project: values.project,
      workItemType: values.workItemType,
      tenantId: values.tenantId,
      clientId: values.clientId,
      ...(values.clientSecret ? { clientSecret: values.clientSecret } : {}),
    };
    try {
      if (connection) await updateIntegrationConnection(connection.id, { name: values.name, config });
      else await createIntegrationConnection({ provider: "azure_devops", name: values.name, config: { ...config, clientSecret: values.clientSecret }, mappings: {} });
      toast.success(connection ? "Integration updated." : "Integration added.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save integration.");
    }
  }));

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      {textFieldKeys.map((key) => (
        <FormField key={key} id={`integration-${key}`} label={textFieldLabels[key]} registration={register(key)} error={errors[key]} />
      ))}
      <FormField
        id="integration-secret"
        label={`Client secret ${connection ? "(leave blank to keep current)" : ""}`}
        registration={register("clientSecret")}
        error={errors.clientSecret}
        type="password"
        autoComplete="new-password"
      />
      <div className="flex gap-2">
        <Button type="submit" disabled={isSubmitting} loading={action.saving} loadingLabel={"Saving…"}>
          {connection ? "Save changes" : "Add integration"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
